/* eslint-disable complexity */
import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { useLocalizedHref } from "@/libraries/web-framework";
import { PatternLogos, InlineLogoProps } from "@/patterns/wpl/logos/src";

const GetInlineLogosProp = () => [
    {
        link: useLocalizedHref("/customers/kiva"),
        src: "https://wac-cdn.atlassian.com/dam/jcr:77c2f652-ab9b-4619-b175-c3f408c3e350/kiva.svg",
        alt: "Kiva",
        width: "128px"
    },
    {
        link: useLocalizedHref("/customers/twitter"),
        src: "https://wac-cdn.atlassian.com/dam/jcr:0a7dd09c-7f87-4bc9-8b65-318b0a41efb8/twitter.svg",
        alt: "Twitter",
        width: "74px"
    },
    {
        link: useLocalizedHref("/customers/redfin"),
        src: "https://wac-cdn.atlassian.com/dam/jcr:46501297-a5e1-482a-9f8f-3718dd24376e/redfin.svg",
        alt: "Redfin",
        width: "170px"
    },
    {
        link: useLocalizedHref("/customers/nasa"),
        src: "https://wac-cdn.atlassian.com/dam/jcr:67ac3b31-6097-49f8-b1c2-efdc492ddae1/nasa.svg",
        alt: "NASA",
        width: "114px"
    },
    {
        link: useLocalizedHref("/customers/audi"),
        src: "https://wac-cdn.atlassian.com/dam/jcr:debe18ec-0a3d-4bfc-aa58-f11948d8d056/audi.svg",
        alt: "Audi",
        width: "140px"
    },
    {
        link: useLocalizedHref("/customers/castlight"),
        src: "https://wac-cdn.atlassian.com/dam/jcr:63951eab-b7d9-4d5f-ab23-ba511d27d4aa/castlight.svg",
        alt: "Castlight",
        width: "240px"
    }
];

const patternLogosTextHomePageStyle = css({
    "backgroundImage": "linear-gradient(180deg, #ffffff 0.05%, #deebff 99.95%)",
    [media.above.xxs]: {
        padding: `${token("space.400")} 0 ${token("space.400")}`
    },
    [media.only.sm]: {
        padding: `${token("space.600")} 0 ${token("space.600")}`
    },
    [media.above.md]: {
        padding: `${token("space.800")} 0 ${token("space.800")}`
    },
    ".text-section": {
        "h2": {
            color: "#253858",
            marginBlockStart: 0,
            letterSpacing: "0.3px",
            marginBlockEnd: "14px",
            [media.above.sm]: {
                fontSize: "1.25rem",
                lineHeight: 1.2
            },
            [media.below.sm]: {
                fontSize: "1rem",
                lineHeight: 1.25
            },
            [media.above.md]: {
                marginBlockEnd: token("space.300")
            }
        },
        ".link-wrapper a": {
            margin: token("space.0"),
            fontWeight: 400
        }
    },
    ".row-layout": {
        [media.above.xs]: {
            paddingBlockStart: token("space.200"),
            paddingBlockEnd: token("space.200")
        },
        ".logos-content": {
            "display": "flex",
            "flexWrap": "wrap",
            "gap": 0,
            "alignItems": "center",
            "justifyContent": "space-evenly",

            // hardcoding max-width here to prevent single "orphans" wrapping to their own line
            [`@media (min-width: 850px) and (max-width: 1245px)`]: {
                maxWidth: "820px",
                margin: "0 auto"
            },
            "@media (max-width: 695px)": {
                maxWidth: "490px",
                margin: "0 auto"
            },
            ".logoblock": {
                "display": "flex",
                "objectFit": "contain",
                "padding": "20px",
                "+ img": {
                    marginBlockEnd: token("space.200"),
                    verticalAlign: "bottom",
                    pointerEvents: "none",
                    maxWidth: "100%"
                }
            }
        }
    },
    ".row-layout:last-child": {
        marginBlockStart: token("space.0")
    },
    ".contentBlock-linkFlex > .link-wrapper > a": {
        "&:hover, &:active": {
            svg: {
                transform: "translate3d(5px, 0, 0)",
                transition: "transform 250ms"
            }
        }
    }
});

export const SocialProof = () => {
    const intl = useIntl();
    const patternLogoBodyText = intl.formatMessage({
        id: "homePage.socialProof.bodyText",
        defaultMessage:
            "Join the 250,000+ companies that use our software to power team collaboration"
    });
    const ctaPropsLinkProps = {
        text: intl.formatMessage({
            id: "homePage.socialProof.ctaPropsLinkText",
            defaultMessage: "See all customers"
        }),
        href: useLocalizedHref("/customers/search")
    };
    const inlineLogosProp: InlineLogoProps[] = GetInlineLogosProp();

    return (
        <UFOSegment name="social-proof">
            <PatternLogos
                css={patternLogosTextHomePageStyle}
                inlineLogos={inlineLogosProp}
                ctaProps={{
                    primaryLinkProps: ctaPropsLinkProps
                }}
                headerText={patternLogoBodyText}
            />
        </UFOSegment>
    );
};
